// Pilotage des abonnés (conception du 27/10/2026, docs/superpowers/specs/2026-10-27-pilotage-abonnes-design.md) :
// tous les comptes, leurs dépenses mesurées et les signaux, pour le seul gestionnaire (`canAccess`, ressource
// `subscribers`) ; la base refuse aussi `manager_accounts` à tout autre compte. Sur ordinateur seulement.
import { useQuery } from "@tanstack/react-query";
import {
  useCanAccess,
  useGetIdentity,
  useGetOne,
  useLocaleState,
  useTranslate,
} from "ra-core";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { Navigate } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useIsMobile } from "@/hooks/use-mobile";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Sale } from "../types";
import type { AccountCurrency } from "./accountCurrency";
import { formatDay } from "./projectUtils";
import { AirscaleCounter } from "./AirscaleCounter";
import { SubscriberPanel } from "./SubscriberPanel";
import {
  formatCost,
  formatUsd,
  matchesSearch,
  monthOptions,
  sortSides,
  type Accounts,
  type Side,
} from "./subscribers";

export type Selection = { kind: "side" | "person"; id: number } | null;

export const SubscribersPage = () => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const isMobile = useIsMobile();
  const { data: identity } = useGetIdentity();
  const { data: me } = useGetOne<Sale>(
    "sales",
    { id: identity?.id ?? "" },
    { enabled: !!identity },
  );
  const currency: AccountCurrency = me?.currency === "USD" ? "USD" : "EUR";
  const months = useMemo(() => monthOptions(new Date()), []);
  const [month, setMonth] = useState(months[0]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [selected, setSelected] = useState<Selection>(null);

  // Écran réservé au gestionnaire (règle `subscribers` de canAccess) : une adresse tapée mène à l'accueil.
  const { canAccess, isPending: accessPending } = useCanAccess({
    resource: "subscribers",
    action: "list",
  });

  const { data, error, isError, isPending, refetch } = useQuery({
    queryKey: ["manager_accounts", month],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc(
        "manager_accounts",
        { month },
      );
      if (error) throw error;
      return data as Accounts;
    },
    enabled: !isMobile && canAccess === true,
    retry: false,
  });

  if (isMobile)
    return (
      <p className="p-4 text-sm">
        {translate("resources.projects.subscribers.desktop_only")}
      </p>
    );
  if (accessPending) return null;
  if (!canAccess) return <Navigate to="/" replace />;
  if (isError)
    return (
      <p className="p-4 text-sm text-destructive">
        {translate("resources.projects.subscribers.error", {
          message: error instanceof Error ? error.message : String(error),
        })}
      </p>
    );
  if (isPending || !data) return null;

  const rate = data.usd_per_eur;
  const cost = (usd: number) => (
    <span title={formatUsd(usd, locale)}>
      {formatCost(usd, currency, rate, locale)}
    </span>
  );
  const sides = sortSides(data.sides).filter((side) =>
    matchesSearch(side, query),
  );
  const sideName = (side: Side) =>
    side.name ?? translate("resources.projects.subscribers.client_team");
  const activity = (value: string | null) =>
    value
      ? formatDay(value, locale)
      : translate("resources.projects.subscribers.never");
  const toggle = (id: number) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const aiLeft =
    data.totals.ai_budget_usd == null
      ? null
      : data.totals.ai_budget_usd - data.totals.ai_spent_usd;

  return (
    <div className="flex gap-4 p-4">
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <div>
          <h1 className="text-xl font-semibold">
            {translate("resources.projects.subscribers.title")}
          </h1>
          <p className="text-muted-foreground text-sm">
            {translate("resources.projects.subscribers.intro")}
          </p>
          {rate == null && (
            <p className="text-sm text-amber-700">
              {translate("resources.projects.subscribers.no_rate")}
            </p>
          )}
        </div>

        <div className="grid grid-cols-5 gap-3">
          <Total
            label="totals.accounts"
            value={cost(data.totals.accounts_usd)}
          />
          <Total
            label="totals.unattributed"
            value={cost(data.totals.unattributed_usd)}
            help={translate(
              "resources.projects.subscribers.totals.unattributed_help",
            )}
          />
          <Total
            label="totals.common"
            value={cost(data.totals.common_usd)}
            help={
              aiLeft == null
                ? undefined
                : translate("resources.projects.subscribers.totals.ai_left", {
                    amount: formatCost(aiLeft, currency, rate, locale),
                  })
            }
          />
          <Total
            label={
              data.totals.airscale_balance == null
                ? "totals.airscale_unknown"
                : "totals.airscale"
            }
            value={data.totals.airscale_balance ?? "—"}
            help={
              data.totals.airscale_read_at
                ? formatDay(data.totals.airscale_read_at, locale)
                : undefined
            }
          />
          <Total
            label="totals.active"
            value={data.totals.active_accounts ?? "—"}
            help={
              data.totals.active_accounts == null
                ? translate(
                    "resources.projects.subscribers.totals.active_current_only",
                  )
                : undefined
            }
          />
        </div>

        <AirscaleCounter
          month={month}
          currency={currency}
          rate={rate}
          locale={locale}
        />

        <Card>
          <CardContent className="flex flex-wrap gap-2 py-3">
            <span className="font-medium">
              {translate("resources.projects.subscribers.signals.title")}
            </span>
            {data.signals.length === 0 && (
              <span className="text-muted-foreground text-sm">
                {translate("resources.projects.subscribers.signals.none")}
              </span>
            )}
            {data.signals.map((signal, index) => {
              const side = data.sides.find((s) => s.id === signal.side);
              const who = signal.sales_id
                ? side?.people.find((p) => p.sales_id === signal.sales_id)
                : undefined;
              return (
                <button
                  key={index}
                  type="button"
                  className="rounded-md focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() =>
                    setSelected(
                      signal.sales_id
                        ? { kind: "person", id: signal.sales_id }
                        : { kind: "side", id: signal.side },
                    )
                  }
                >
                  <Badge variant="outline" className="cursor-pointer">
                    {translate(
                      `resources.projects.subscribers.signals.${signal.kind}`,
                      {
                        amount:
                          rate == null
                            ? new Intl.NumberFormat(locale, {
                                style: "currency",
                                currency: "EUR",
                                currencyDisplay: "narrowSymbol",
                                maximumFractionDigits: 0,
                              }).format(data.spend_alert_eur)
                            : formatCost(
                                data.spend_alert_eur * rate,
                                currency,
                                rate,
                                locale,
                              ),
                      },
                    )}
                    {" · "}
                    {who
                      ? [who.first_name, who.last_name]
                          .filter(Boolean)
                          .join(" ") || who.email
                      : side
                        ? sideName(side)
                        : ""}
                  </Badge>
                </button>
              );
            })}
          </CardContent>
        </Card>

        <div className="flex gap-2">
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={translate("resources.projects.subscribers.search")}
            className="max-w-sm"
          />
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map((value) => (
                <SelectItem key={value} value={value}>
                  {new Date(value).toLocaleDateString(locale, {
                    month: "long",
                    year: "numeric",
                    timeZone: "UTC",
                  })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Fiche ouverte : le tableau perd un tiers de sa largeur ; cellules resserrées pour garder la date entière. */}
        <Table
          className={
            selected
              ? "text-xs [&_td]:px-1 [&_th]:px-1 [&_th]:whitespace-normal"
              : undefined
          }
        >
          <TableHeader>
            <TableRow>
              {(
                [
                  "account",
                  "offer",
                  "people",
                  "balance",
                  "credits",
                  "cost",
                  "activity",
                ] as const
              ).map((key) => (
                <TableHead key={key}>
                  {translate(`resources.projects.subscribers.columns.${key}`)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sides.map((side) => (
              <Fragment key={side.id}>
                <TableRow
                  className="cursor-pointer"
                  onClick={() => setSelected({ kind: "side", id: side.id })}
                >
                  <TableCell>
                    <button
                      type="button"
                      className="mr-1 align-middle"
                      aria-label={translate(
                        open.has(side.id)
                          ? "resources.projects.subscribers.collapse"
                          : "resources.projects.subscribers.expand",
                      )}
                      aria-expanded={open.has(side.id)}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggle(side.id);
                      }}
                    >
                      {open.has(side.id) ? (
                        <ChevronDown className="size-4" />
                      ) : (
                        <ChevronRight className="size-4" />
                      )}
                    </button>
                    {sideName(side)}
                  </TableCell>
                  <TableCell>
                    {side.partner && side.partner_offer
                      ? translate(
                          `resources.projects.offer.${side.partner_offer}`,
                        )
                      : ""}
                  </TableCell>
                  <TableCell>{side.members}</TableCell>
                  <TableCell>{side.balance}</TableCell>
                  <TableCell>{side.credits_spent}</TableCell>
                  <TableCell>{cost(side.cost_usd)}</TableCell>
                  <TableCell>{activity(side.last_activity)}</TableCell>
                </TableRow>
                {open.has(side.id) &&
                  side.people.map((person) => (
                    <TableRow
                      key={person.sales_id}
                      className="bg-muted/40 cursor-pointer"
                      onClick={() =>
                        setSelected({ kind: "person", id: person.sales_id })
                      }
                    >
                      <TableCell className="pl-10!">
                        {[person.first_name, person.last_name]
                          .filter(Boolean)
                          .join(" ") || person.email}
                        {person.disabled && (
                          <span className="text-muted-foreground ml-2 text-xs">
                            {translate(
                              "resources.projects.subscribers.suspended",
                            )}
                          </span>
                        )}
                      </TableCell>
                      <TableCell
                        className="text-muted-foreground max-w-40 truncate text-xs"
                        title={person.email}
                      >
                        {person.email}
                      </TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell>{person.credits_spent}</TableCell>
                      <TableCell>{cost(person.cost_usd)}</TableCell>
                      <TableCell>{activity(person.last_activity)}</TableCell>
                    </TableRow>
                  ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
      {selected && (
        <SubscriberPanel
          selected={selected}
          month={month}
          currency={currency}
          rate={rate}
          sideNames={Object.fromEntries(
            data.sides.map((side) => [side.id, sideName(side)]),
          )}
          onSelect={setSelected}
          onClose={() => setSelected(null)}
          onChange={() => refetch()}
        />
      )}
    </div>
  );
};

const Total = ({
  label,
  value,
  help,
}: {
  label: string;
  value: React.ReactNode;
  help?: string;
}) => {
  const translate = useTranslate();
  return (
    <Card>
      <CardContent className="py-3">
        <div className="text-muted-foreground text-xs">
          {translate(`resources.projects.subscribers.${label}`)}
        </div>
        <div className="text-lg font-semibold">{value}</div>
        {help && <div className="text-muted-foreground text-xs">{help}</div>}
      </CardContent>
    </Card>
  );
};

SubscribersPage.path = "/abonnes";
